<template>
  <el-container class="setting-page">
    <el-aside width="230px" class="setting-aside" :class="{ fullscreen: configStore.fullScreen }">
      <div class="aside-header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
        <span class="aside-title">{{ $t('setting.title') }}</span>
      </div>
      <div class="nav-menu">
        <div v-for="(item, index) in displayMenuItems" :key="index" class="nav-item"
          :class="{ active: currentTab === index }" @click="switchTab(index)">
          <i :class="item.icon"></i>
          <span>{{ item.label }}</span>
        </div>
      </div>
    </el-aside>

    <el-container class="setting-main">
      <div class="content-header" :class="configStore.electronMode ? 'header-menu-wrap' : ''">
        <div class="header-title">{{ currentTabItem.label }}</div>
      </div>
      <div class="content-body">
        <div v-show="currentComponent === 'personalInfo'" class="tab-content">
          <personal-info ref="personalInfo" @switch-tab="onSwitchTab"></personal-info>
        </div>

        <div v-show="currentComponent === 'userConfig'" class="tab-content">
          <user-config ref="userConfig"></user-config>
        </div>

        <div v-show="currentComponent === 'bindPhone'" class="tab-content">
          <bind-phone ref="bindPhone"></bind-phone>
        </div>

        <div v-show="currentComponent === 'bindEmail'" class="tab-content">
          <bind-email ref="bindEmail"></bind-email>
        </div>

        <div v-show="currentComponent === 'modifyPassword'" class="tab-content">
          <modify-password ref="modifyPassword"></modify-password>
        </div>

        <div v-show="currentComponent === 'realnameAuth'" class="tab-content">
          <realname-auth ref="realnameAuth"></realname-auth>
        </div>

        <div v-show="currentComponent === 'aboutUs'" class="tab-content" v-if="configStore.electronMode">
          <about-us ref="aboutUs"></about-us>
        </div>
      </div>
    </el-container>
  </el-container>
</template>

<script>
import ModifyPassword from '../components/setting/ModifyPassword.vue';
import PersonalInfo from '../components/setting/PersonalInfo.vue';
import UserConfig from '../components/setting/UserConfig.vue';
import BindPhone from '../components/setting/BindPhone.vue';
import BindEmail from '../components/setting/BindEmail.vue';
import RealnameAuth from '../components/setting/RealnameAuth.vue';
import AboutUs from '../components/setting/AboutUs.vue';

export default {
  name: "SettingPage",
  components: {
    PersonalInfo,
    UserConfig,
    BindPhone,
    BindEmail,
    ModifyPassword,
    RealnameAuth,
    AboutUs
  },
  data() {
    return {
      currentTab: 0
    }
  },
  computed: {
    menuItems() {
      const items = [
        {
          label: this.$t('setting.personalInfo'),
          icon: 'el-icon-user',
          component: 'personalInfo'
        },
        {
          label: this.$t('setting.userConfig'),
          icon: 'el-icon-setting',
          component: 'userConfig'
        },
        {
          label: this.$t('setting.bindPhone'),
          icon: 'el-icon-mobile-phone',
          component: 'bindPhone',
          show: () => this.configStore.registration.mode.includes('phone')
        },
        {
          label: this.$t('setting.bindEmail'),
          icon: 'el-icon-message',
          component: 'bindEmail',
          show: () => this.configStore.registration.mode.includes('email')
        },
        {
          label: this.$t('setting.modifyPassword'),
          icon: 'el-icon-lock',
          component: 'modifyPassword'
        },
        {
          label: this.$t('setting.realnameAuth'),
          icon: 'el-icon-postcard',
          component: 'realnameAuth'
        }
      ];

      if (this.configStore.electronMode) {
        items.push({
          label: this.$t('setting.aboutUs'),
          icon: 'el-icon-info',
          component: 'aboutUs'
        });
      }

      return items;
    },
    displayMenuItems() {
      return this.menuItems.filter(item => !item.show || item.show());
    },
    currentTabItem() {
      return this.displayMenuItems[this.currentTab] || this.displayMenuItems[0];
    },
    currentComponent() {
      const item = this.displayMenuItems[this.currentTab];
      return item ? item.component : '';
    }
  },
  mounted() {
    this.init();
    this.$eventBus.$on('switchToPersonalInfo', () => {
      this.switchTab(0);
    });
  },
  methods: {
    init() {
      const tab = this.$route.query.tab;
      if (tab) {
        this.currentTab = parseInt(tab);
      }
      this.$nextTick(() => this.onClickTab());
    },
    switchTab(index) {
      this.currentTab = index;
      this.$nextTick(() => this.onClickTab());
    },
    onClickTab() {
      const componentName = this.currentComponent;
      if (componentName && this.$refs[componentName] && this.$refs[componentName].init) {
        this.$refs[componentName].init();
      }
    },
    onSwitchTab(tabIndex) {
      this.currentTab = parseInt(tabIndex);
      this.$nextTick(() => this.onClickTab());
    }
  },
  beforeDestroy() {
    this.$eventBus.$off('switchToPersonalInfo');
  }
}
</script>

<style lang="scss" scoped>
.setting-page {
  height: 100vh;
  background: #f5f6fa;

  .setting-aside {
    display: flex;
    flex-direction: column;
    height: 100%;
    border-right: 1px solid rgba(0, 0, 0, 0.08);
    box-shadow: 2px 0 8px rgba(0, 0, 0, 0.05);
    background: white;

    &.fullscreen {
      width: 260px !important;

      @media (min-width: 1200px) {
        width: 290px !important;
      }
    }

    .aside-header {
      height: 60px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      padding: 0 20px;
      border-bottom: var(--im-border);

      .aside-title {
        font-size: var(--im-font-size-larger);
        font-weight: 600;
        color: #333;
      }
    }

    .nav-menu {
      flex: 1;
      overflow-y: auto;
      padding: 5px 0;

      .nav-item {
        display: flex;
        align-items: center;
        gap: 10px;
        height: 50px;
        margin: 0 3px;
        padding: 5px 12px;
        cursor: pointer;
        border-radius: 10px;
        white-space: nowrap;

        &:hover {
          background-color: var(--im-background-active);
        }

        &.active {
          background-color: var(--im-background-active-dark);
        }

        i {
          font-size: 18px;
          width: 20px;
          text-align: center;
        }

        span {
          font-size: var(--im-font-size);
        }
      }
    }
  }

  .setting-main {
    display: flex;
    flex-direction: column;
    background: #fcfdff;
    overflow: hidden;

    .content-header {
      height: 60px;
      flex-shrink: 0;
      display: flex;
      align-items: center;
      padding: 0 20px;
      border-bottom: var(--im-border);
      background: white;

      .header-title {
        font-size: var(--im-font-size-larger);
        font-weight: 600;
        color: #333;
      }
    }

	.content-body {
			flex: 1;
			overflow: hidden;
			background: #fafbfc;
			min-height: 0;
		}

	.tab-content {
			height: 100%;
			overflow-y: auto;
		}
  }
}
</style>
